import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  useReactTable,
  getCoreRowModel,
  type SortingState,
  type RowSelectionState,
  type VisibilityState,
} from '@tanstack/react-table'
import { Header } from '@/components/layout/header'
import { DataTable } from '@/components/ui/data-table'
import { PackageSummaryCards } from '@/components/packages/package-summary-cards'
import { PackageToolbar, type PackageDatePreset } from '@/components/packages/package-toolbar'
import { getPackageColumns, PACKAGE_HIDDEN_COLUMNS } from '@/components/packages/package-columns'
import { usePackages } from '@/hooks/usePackages'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import { api, type PackageItemType, type PackagePatientRow, type PackageRow, type PackageView, type PackagesListParams } from '@/lib/api-client'

export const Route = createFileRoute('/packages')({
  component: PackagesPage,
})

const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

/** Preset → from/to YYYY-MM-DD (MYT). */
function presetToRange(preset: PackageDatePreset, customFrom: string, customTo: string): { from?: string; to?: string } {
  if (preset === 'all') return {}
  if (preset === 'custom') return { from: customFrom || undefined, to: customTo || undefined }
  const myt = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }))
  const ymd = (d: Date) => d.toLocaleDateString('en-CA')
  const months = preset === '18months' ? 18 : 12
  const start = new Date(myt)
  start.setMonth(start.getMonth() - months)
  return { from: ymd(start) }
}

function PackagesPage() {
  const { branchId } = useBranch()
  const branchIdNum = BRANCH_IDS[branchId]

  // ---- Filter state ----
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [datePreset, setDatePreset] = React.useState<PackageDatePreset>('18months')
  const [customFrom, setCustomFrom] = React.useState('')
  const [customTo, setCustomTo] = React.useState('')
  const [itemTypes, setItemTypes] = React.useState<PackageItemType[]>([]) // [] = all
  const [outstandingOnly, setOutstandingOnly] = React.useState(true)
  const [view, setView] = React.useState<PackageView>('patients')

  // ---- Table state (rows view) ----
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(50)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    Object.fromEntries(PACKAGE_HIDDEN_COLUMNS.map((c) => [c, false])),
  )

  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  React.useEffect(() => {
    setPage(1)
  }, [branchId, datePreset, customFrom, customTo, itemTypes, outstandingOnly, view])

  const range = presetToRange(datePreset, customFrom, customTo)
  const sortId = sorting[0]?.id as PackagesListParams['sortBy'] | undefined
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'

  const params: PackagesListParams = {
    branchId: branchIdNum,
    from: range.from,
    to: range.to,
    itemType: itemTypes.length > 0 ? itemTypes.join(',') : undefined,
    outstandingOnly,
    search: debouncedSearch || undefined,
    view,
    page,
    pageSize,
    sortBy: view === 'rows' ? sortId : undefined,
    sortOrder: sorting.length > 0 ? sortOrder : undefined,
  }

  const { data, isLoading } = usePackages(params)

  const columns = React.useMemo(() => getPackageColumns(), [])

  const responseView = data?.view
  const tableData: PackageRow[] = data?.view === 'rows' ? (data.data as PackageRow[]) : []
  const table = useReactTable({
    data: tableData,
    columns,
    pageCount: data?.pageCount ?? -1,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: { pageIndex: page - 1, pageSize },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize }) : updater
      setPage(next.pageIndex + 1)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const handleExport = React.useCallback(() => {
    // Export always reflects the current filter window as a per-patient call list.
    window.open(api.packages.getExportUrl({ ...params, page: undefined, pageSize: undefined }), '_blank')
  }, [params])

  const subtitle = data
    ? view === 'patients'
      ? `${data.total.toLocaleString()} patients · ${data.summary.treatmentBalance.toLocaleString()} treatment sessions outstanding`
      : `${data.total.toLocaleString()} package items`
    : 'Loading...'

  return (
    <div className="flex flex-col h-full">
      <Header title="Packages" subtitle={subtitle} />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4 overflow-y-auto">
        <PackageSummaryCards data={data} isLoading={isLoading} />

        <PackageToolbar
          search={search}
          onSearchChange={setSearch}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          itemTypes={itemTypes}
          onItemTypesChange={setItemTypes}
          outstandingOnly={outstandingOnly}
          onOutstandingOnlyChange={setOutstandingOnly}
          view={view}
          onViewChange={setView}
          onExport={handleExport}
          table={table}
        />

        <FilterBanner
          total={data?.total}
          unfilteredTotal={data?.unfilteredTotal}
          onShowAllTime={() => {
            setDatePreset('all')
            setCustomFrom('')
            setCustomTo('')
          }}
          onClearFilters={() => {
            setSearch('')
            setDatePreset('all')
            setCustomFrom('')
            setCustomTo('')
            setItemTypes([])
          }}
        />

        {view === 'rows' ? (
          <DataTable
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            pageCount={data?.pageCount}
            pageIndex={page - 1}
            pageSize={pageSize}
            onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
            sorting={sorting}
            onSortingChange={setSorting}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            emptyMessage="No package items. Adjust filters or wait for the next Avixo sync."
          />
        ) : (
          <CallListTable
            rows={data?.view === 'patients' ? (data.data as PackagePatientRow[]) : []}
            isLoading={isLoading || responseView !== 'patients'}
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  )
}

// ============================================================================
// FILTER BANNER — "X of Y hidden" when date/type/search trims >30% of scope
// ============================================================================

function FilterBanner({
  total,
  unfilteredTotal,
  onClearFilters,
  onShowAllTime,
}: {
  total: number | undefined
  unfilteredTotal: number | undefined
  onClearFilters: () => void
  onShowAllTime: () => void
}) {
  if (total === undefined || unfilteredTotal === undefined) return null
  if (unfilteredTotal === 0) return null
  if (total / unfilteredTotal >= 0.7) return null

  const hidden = unfilteredTotal - total
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-info/30 bg-info/10 px-3 py-2 text-[13px]">
      <span className="text-text-dark">
        Showing <strong>{total.toLocaleString()}</strong> of <strong>{unfilteredTotal.toLocaleString()}</strong>
        <span className="text-text-muted"> · {hidden.toLocaleString()} hidden by current filters</span>
      </span>
      <div className="flex items-center gap-2">
        <button onClick={onShowAllTime} className="rounded-full bg-card border border-border px-3 py-1 text-[12px] hover:bg-hover">Show all time</button>
        <button onClick={onClearFilters} className="rounded-full bg-info text-white px-3 py-1 text-[12px] hover:bg-info/90">Clear filters</button>
      </div>
    </div>
  )
}

// ============================================================================
// CALL LIST — per-patient, ranked by outstanding treatment balance (priority)
// ============================================================================

function fmtDate(v: string | null): string {
  if (!v) return '-'
  const d = new Date(v)
  if (isNaN(d.getTime())) return v
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' })
}

function CallListTable({
  rows,
  isLoading,
  page,
  pageSize,
  total,
  onPageChange,
}: {
  rows: PackagePatientRow[]
  isLoading: boolean
  page: number
  pageSize: number
  total: number
  onPageChange: (p: number) => void
}) {
  if (isLoading) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">Loading call list...</div>
  }
  if (rows.length === 0) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">No patients with outstanding packages in this window.</div>
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const startRank = (page - 1) * pageSize

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background/50 border-b border-border">
              <tr className="text-left text-[11px] uppercase tracking-wide text-text-muted">
                <th className="px-3 py-2.5 w-10">#</th>
                <th className="px-3 py-2.5">Patient</th>
                <th className="px-3 py-2.5">Phone</th>
                <th className="px-3 py-2.5 text-center">Packages</th>
                <th className="px-3 py-2.5 text-right">Treatment Bal.</th>
                <th className="px-3 py-2.5 text-right">Total Bal.</th>
                <th className="px-3 py-2.5">Last Purchase</th>
                <th className="px-3 py-2.5">Branch</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.branchId}-${r.pno}`} className="border-b border-border last:border-0 hover:bg-hover/30">
                  <td className="px-3 py-3 align-top text-[12px] text-text-muted font-mono">{startRank + i + 1}</td>
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-[13px]">{r.patientName ?? <span className="italic text-text-muted">(unknown)</span>}</div>
                    <div className="text-xs text-text-muted font-mono">{r.pno}</div>
                  </td>
                  <td className="px-3 py-3 align-top text-[13px]">
                    {r.phone ?? <span className="text-text-muted">-</span>}
                  </td>
                  <td className="px-3 py-3 align-top text-center text-[13px] font-mono">{r.packageCount}</td>
                  <td className="px-3 py-3 align-top text-right">
                    <span className="text-[15px] font-bold text-info">{r.treatmentBalance}</span>
                  </td>
                  <td className="px-3 py-3 align-top text-right text-[13px] font-mono text-text-muted">{r.totalBalance}</td>
                  <td className="px-3 py-3 align-top text-[13px] whitespace-nowrap">{fmtDate(r.lastPurchase)}</td>
                  <td className="px-3 py-3 align-top text-xs text-text-muted">{r.branchName ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[13px]">
        <span className="text-text-muted">
          {total.toLocaleString()} patients · page {page} of {pageCount}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-full border border-border bg-card px-3 py-1 text-[12px] disabled:opacity-40 hover:bg-hover"
          >
            Previous
          </button>
          <button
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            className="rounded-full border border-border bg-card px-3 py-1 text-[12px] disabled:opacity-40 hover:bg-hover"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
